<template>
  <BaseModal
    card-width="520px"
    no-padding
    no-toolbar
    no-exit
    @close="$emit('close')"
  >
    <template #default>
      <!-- Modal Header -->
      <div class="relative w-full flex items-start gap-1 p-5 border-b border-border-normal">
        <ChairIcon class="rounded-md w-5 h-5" />
        <div class="space-y-1">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Seat Settings
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal"
          >
            Adjust the number of paid seats available for your team.
          </BaseText>
        </div>
        <button
          class="absolute top-3 right-3 close-btn"
          @click="$emit('close')"
        >
          <TagRemoveIcon
            stroke="#5E6678"
            :width="20"
            :height="20"
          />
        </button>
      </div>
      <!-- Modal Content -->
      <div class="w-full flex flex-col gap-1 px-5 pt-5">
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted"
        >
          {{ teamAllocation.maxFree }} seats are included in your plan.
        </BaseText>
        <div class="bg-background-normal h-0.5 w-full my-5" />
        <div class="flex items-center justify-between gap-2">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Additional seats
          </BaseText>
          <BaseText
            size="sm"
            class="text-text-normal flex-1"
          >
            ${{ seatCostPerInterval }}/{{ getUserSubscription?.interval === 'year' ? 'yr' : 'mo' }} per seat.
          </BaseText>
          <div class="border border-border-normal p-1 flex items-center counter-container">
            <button
              class="change-btn"
              :disabled="newTeamCount === teamAllocation.maxFree"
              @click="newTeamCount--"
            >
              <MinusIcon />
            </button>
            <BaseText class="change-number px-3 min-w-[28px] text-center">
              {{ newTeamCount }}
            </BaseText>
            <button
              class="change-btn"
              @click="newTeamCount++"
            >
              <PlusIcon />
            </button>
          </div>
        </div>
        <div
          class="p-2 rounded-lg w-full mt-2"
          :class="`alert-${changeMessage.severity}`"
        >
          <BaseText
            type="body"
            size="sm"
            class="whitespace-pre-wrap"
            :class="`text-${changeMessage.severity}`"
          >
            {{ changeMessage.message }}
          </BaseText>
        </div>
        <!--  -->
      </div>
      <div class="px-5">
        <div
          class="bg-background-normal h-0.5 w-full my-5"
          :class="approximateProratedCost < 0 ? 'opacity-0' : ''"
        />
      </div>
      <div
        v-if="approximateProratedCost >= 0"
        class="w-full flex items-start justify-between px-5"
      >
        <div class="space-y-3">
          <BaseText
            size="sm"
            class="text-text-normal"
          >
            The total will be charged after confirmation
          </BaseText>
          <div class="flex items-center gap-1.5 text-text-normal">
            <InfoSimpleIcon />
            <BaseText
              type="label"
              size="xs"
            >
              Estimated prorated cost for the rest of your billing period
            </BaseText>
          </div>
        </div>
        <div class="flex items-center gap-1 text-text-normal">
          <BaseText
            class="text-text-muted font-medium"
          >
            ~${{ approximateProratedCost }}
          </BaseText>
        </div>
      </div>
      <div
        class="bg-background-normal h-0.5 w-full my-5"
      />
      <!-- Modal Actions Footer -->
      <div class="w-full flex justify-between p-5">
        <button
          class="px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('close')"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Cancel
          </BaseText>
        </button>
        <button
          class="submit-btn py-1.5 px-3 rounded-md"
          :disabled="!isFormValid.bool || loadingSubmit"
          :class="isFormValid.bool ? 'enabled' : 'disabled cursor-default'"
          @click="updateTeamSeat"
        >
          <BaseLoadingSpinner
            v-if="loadingSubmit"
            small
          />
          <BaseText
            v-else
            type="label"
            size="sm"
          >
            Update
          </BaseText>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { mapGetters, mapMutations } from 'vuex'
// Icons
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import MinusIcon from '../../globals/Icons/MathOperators/MinusIcon.vue'
import PlusIcon from '../../globals/Icons/MathOperators/PlusIcon.vue'
import ChairIcon from '../../globals/Icons/ChairIcon.vue'
import InfoSimpleIcon from '../../globals/Icons/InfoSimpleIcon.vue'
export default {
  name: 'TeamSeatModal',
  components: {
    TagRemoveIcon,
    MinusIcon,
    PlusIcon,
    ChairIcon,
    InfoSimpleIcon
  },
  props: {
    teamAllocation: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      newTeamCount: this.teamAllocation.max,
      loadingSubmit: false,

      today: Math.floor(new Date().getTime() / 1000)
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam', 'getUserSubscription']),
    seatCostPerInterval () {
      const interval = this.getUserSubscription?.interval || 'month'
      // Monthly: $20/month, Annual: $240/year
      return interval === 'year' ? 240 : 20
    },
    approximateProratedCost () {
      const periodStart = this.getUserSubscription?.currentPeriodStart
      const periodEnd = this.getUserSubscription?.currentPeriodEnd
      const totalDuration = periodEnd - periodStart
      const remainingDuration = periodEnd - this.today
      const fullPrice = (this.newTeamCount - this.teamAllocation.max) * this.seatCostPerInterval
      const fractionRemaining = remainingDuration / totalDuration
      const proration = fullPrice * fractionRemaining

      return Number(proration.toFixed(2))
    },
    teamSeatCost () {
      return (this.newTeamCount - this.teamAllocation.max) * this.seatCostPerInterval
    },
    changeMessage () {
      if (this.newTeamCount < this.teamAllocation.current) {
        return {
          message: 'Some seats are still assigned — remove those users to free them up.',
          severity: 'error'
        }
      }

      const totalPaid = this.teamAllocation.paid > 0 ? this.teamAllocation.paid + this.teamAllocation.maxFree : 0
      if (this.newTeamCount < totalPaid) {
        return {
          message: 'Additional seats have been removed.\n The update will apply in your next billing period.',
          severity: 'warning'
        }
      }

      return {
        message: 'Additional seat payments may be billed separately.',
        severity: 'info'
      }
    },
    isFormValid () {
      // Check for too few seats (can't go below current team size)
      if (this.newTeamCount < this.teamAllocation.current) {
        return {
          bool: false,
          severity: 'error',
          message: `You have ${this.teamAllocation.current} team member${this.teamAllocation.current === 1 ? '' : 's'} assigned. You cannot reduce seats below this number.`
        }
      }
      // Check if value has changed
      if (this.newTeamCount !== this.teamAllocation.max) {
        return {
          bool: true,
          severity: 'success',
          message: 'You can update your additional seats allocation.'
        }
      }
      // No change
      return {
        bool: false,
        severity: 'info',
        message: 'No changes have been made to seat allocation.'
      }
    }
  },
  mounted () {
    this.newTeamCount = this.teamAllocation.max
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER_SUBSCRIPTION']),
    async updateTeamSeat () {
      this.loadingSubmit = true
      try {
        const { data } = await ForeplayAPI.Subscription.updateTeamSeats(this.newTeamCount)
        this.SET_USER_SUBSCRIPTION({ ...this.getUserSubscription, quantity: data })
        this.$emit('close')
        this.$showAlert({
          type: 'success',
          message: 'Team seats updated successfully.'
        })
      } catch (e) {
        console.error(e)
        const errCode = e?.response?.data?.error || 'unknown'
        let errMessage = 'An unexpected error occurred. Please try again later.'
        if (errCode === 'ADMIN_ONLY_REQUEST') {
          errMessage = 'This action is only available to admins.'
        }
        this.$showAlert({
          type: 'error',
          message: errMessage
        })
      } finally {
        this.loadingSubmit = false
      }
    }
  }

}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
.submit-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}
.change-btn {
  @apply p-1.5 transition-colors flex items-center justify-center;
  aspect-ratio: 1;
  color: #5E6678;
  border-radius: 0.25rem;
}
.change-btn:not(:disabled):hover {
  background-color: #ECEFF3;
}
.change-btn:disabled{
  cursor: not-allowed;
  opacity: 0.25;
}
.change-btn :deep(svg) {
  stroke: #5E6678;
}

.change-number {
  color: rgb(48, 53, 70);
  font-weight: 500;
}

.counter-container {
  border-radius: 0.5rem;
}

.close-btn {
  padding: 0.375rem;
  border-radius: 0.25rem;
  transition: background-color 100ms;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background-color: #ECEFF3;
}

.alert-info{
  background-color: #EEF8FF;
}

.text-info{
  color: #002583;
}

.alert-warning{
  background-color: #FFF6DE;
}

.text-warning{
  color: #66381A;
}

.alert-error{
  background-color: #FFEFF3;
}

.text-error{
  color: #7C001F;
}

</style>
